import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

const LOOKUP_IDENTIFIER = "identifier";
const LOOKUP_TITLE = "title";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

const ENDPOINT_BY_LOOKUP = {
  [LOOKUP_IDENTIFIER]: "/shopify/update-price",
  [LOOKUP_TITLE]: "/shopify/update-price-by-title",
};

// "YYYY-MM-DDTHH:mm" in the user's local time — what <input type="datetime-local"> expects.
const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

// Show in the user's locale. Returns "—" when the input is missing/unparseable.
const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const LOOKUP_HELPER_TEXT = {
  [LOOKUP_IDENTIFIER]: "File needs at least one of: SKU (variant-level — one row → one variant), Parent SKU (product-level — one row → all variants of that product), SHOPIFY_PRODUCT_ID, HANDLE.",
  [LOOKUP_TITLE]: "File needs a 'Product Title' column (also accepts 'Title', 'Name', or 'Product Name'). Title matching is case-insensitive but less precise than identifier lookup.",
};

const LOOKUP_LABEL = {
  [LOOKUP_IDENTIFIER]: "Identifier",
  [LOOKUP_TITLE]: "Title",
};

const LOCATION_ANY = "";
const LOCATION_MAIN = "Main Store";
const LOCATION_OUTLET = "Outlet Store";
const LOCATION_OPTIONS = [
  { value: LOCATION_ANY, label: "Any location (no filter)" },
  { value: LOCATION_MAIN, label: "Main Store only" },
  { value: LOCATION_OUTLET, label: "Outlet Store only" },
];


function PriceUpdateTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [locationFilter, setLocationFilter] = useState(LOCATION_ANY);
  const [lookupMode, setLookupMode] = useState(LOOKUP_IDENTIFIER);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Recomputed on every render so it follows the wall clock. Cheap.
  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/shopify/update-price/jobs",
    cacheKey: "priceUpdate.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Price Update",
    description:
      "Bulk-update Shopify variant prices from an uploaded CSV/XLSX. Runs as a background job — you can leave this page while it runs.",
    steps: [
      "Upload a CSV or XLSX. Identifier mode: at least one of SKU (variant — single variant per row), Parent SKU (fans the price out to every variant of the product), SHOPIFY_PRODUCT_ID, HANDLE. Title mode: TITLE column.",
      "Include the price columns: 'Price' (sets Shopify Compare-at price) and/or 'OUTLET PRICE' (sets Shopify Price). Either or both are accepted per row.",
      "Choose Run now (kicks off immediately) or Schedule for later (pick a date/time — the file uploads now, the job fires at that time).",
      "Click Start / Schedule. You'll get a job ID and live progress once it runs.",
      "Leave the page if you like — jobs run server-side and reappear here when you return.",
      "When done, download the report. It lists each row's status, the matched product, and which variants got updated.",
    ],
    templates: [],
  };

  const guideTips = [
    "Field mapping: file 'OUTLET PRICE' → Shopify price. File 'Price' → Shopify compareAtPrice (the MSRP / strikethrough).",
    "SKU rows update one specific variant. PRODUCT_ID / HANDLE / TITLE rows update ALL variants of the matched product.",
    "Rows where the current values already match the target are reported as 'no_change' — no Shopify write happens.",
    "Scheduled jobs are persisted server-side and survive deploys. Use Cancel on a scheduled job before it runs if plans change.",
    "Once a scheduled job has started it can't be cancelled here — it'll run to completion or failure like any other.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prev = previous[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(`Price update job ${j.job_id} complete. Report is ready.`, "success");
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Price update job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Price update job ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(jobs.map((j) => [j.job_id, j.status]));
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      // 30s buffer so users typing "now-ish" don't bounce off the server clock.
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast("Scheduled date/time must be at least 30 seconds in the future.", "error");
        return;
      }
      scheduledIso = target.toISOString();
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (locationFilter) {
      formData.append("location", locationFilter);
    }
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    const endpoint = ENDPOINT_BY_LOOKUP[lookupMode] || ENDPOINT_BY_LOOKUP[LOOKUP_IDENTIFIER];

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post(endpoint, formData, {
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(pct);
        },
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      if (scheduledIso) {
        addToast(
          `Price update job ${jobId} scheduled for ${formatScheduledDisplay(scheduledIso)}.`,
          "info"
        );
      } else {
        addToast(`Price update job ${jobId} queued. Tracking progress…`, "info");
      }
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to start price update job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled price update job ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`/shopify/update-price/jobs/${job.job_id}`);
      addToast(`Job ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel job.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/update-price/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "price_update_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/update-price/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute("download", extractedFilename || job.input_filename || "input_file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download input file.");
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast("Scheduled date/time must be at least 30 seconds in the future.", "error");
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `/shopify/update-price/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Price update job ${job.job_id} rescheduled for ${formatScheduledDisplay(scheduledIso)}.`
          : `Price update job ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Price Update"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product list"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText={
                  lookupMode === LOOKUP_TITLE
                    ? "CSV or XLSX with a 'Product Title' column (also accepts 'Title', 'Name', or 'Product Name'), plus Price and/or OUTLET PRICE columns."
                    : "CSV or XLSX with at least one of: SKU (variant), Parent SKU (fans the price out to all variants of the product), SHOPIFY_PRODUCT_ID, HANDLE — plus Price and/or OUTLET PRICE columns."
                }
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tab Name <span className="tool-section-optional">(optional)</span></h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank to use the default (a tab named "Variants" if present, otherwise the first tab). Case-sensitive — must match the tab name exactly.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Variants"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Location Filter <span className="tool-section-optional">(optional)</span></h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="location-filter">
                  Only update products tracked at
                </label>
                <select
                  id="location-filter"
                  className="tool-input"
                  value={locationFilter}
                  onChange={(event) => setLocationFilter(event.target.value)}
                  disabled={uploading}
                >
                  {LOCATION_OPTIONS.map((opt) => (
                    <option key={opt.value || "any"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  When set, rows whose matched product isn't tracked at the chosen location (no Shopify inventoryLevel record there) are skipped (status: <code>skipped_other_location</code>). Quantity is ignored — an out-of-stock product still counts as belonging to its tracked location. A product is only skipped when its inventory has been fully moved to the other store. Shopify variant prices are global, so this filters <em>which</em> products receive the update — it doesn't write a per-location price.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Lookup By</h3>
              </div>
              <div className="tool-field-stack">
                <div className="tool-radio-group" role="radiogroup" aria-label="Lookup mode">
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="price-lookup-mode"
                      value={LOOKUP_IDENTIFIER}
                      checked={lookupMode === LOOKUP_IDENTIFIER}
                      onChange={() => setLookupMode(LOOKUP_IDENTIFIER)}
                      disabled={uploading}
                    />
                    <span>Identifier columns</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="price-lookup-mode"
                      value={LOOKUP_TITLE}
                      checked={lookupMode === LOOKUP_TITLE}
                      onChange={() => setLookupMode(LOOKUP_TITLE)}
                      disabled={uploading}
                    />
                    <span>Product title</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    {LOOKUP_HELPER_TEXT[lookupMode]}
                  </span>
                </div>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="price-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="price-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label className="tool-label" htmlFor="price-scheduled-at">
                      Date &amp; time
                    </label>
                    <input
                      id="price-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file is uploaded now and the job will fire at the scheduled time — you can close the page in between.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={uploading}>
                {uploading
                  ? "Uploading…"
                  : runTime === RUN_SCHEDULE
                  ? "Schedule Price Update Job"
                  : "Start Price Update Job"}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Price Update Jobs"
          emptyText="No price update jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                const jobLookup = j.lookup_mode === LOOKUP_TITLE ? LOOKUP_TITLE : LOOKUP_IDENTIFIER;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Lookup</span>
                      <span className="tool-job-value">{LOOKUP_LABEL[jobLookup]}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    {j.sheet_name ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Tab</span>
                        <span className="tool-job-value">{j.sheet_name}</span>
                      </div>
                    ) : null}
                    {j.scheduled_at ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Scheduled</span>
                        <span className="tool-job-value">
                          {formatScheduledDisplay(j.scheduled_at)}
                        </span>
                      </div>
                    ) : null}
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : null}
                    <div className="tool-job-counts">
                      <span>Updated: {progress.updated || 0}</span>
                      <span>No change: {progress.no_change || 0}</span>
                      <span>Not found: {progress.not_found || 0}</span>
                      <span>Errors: {progress.errors || 0}</span>
                      <span>Skipped: {progress.skipped || 0}</span>
                      <span>Invalid price: {progress.invalid_price || 0}</span>
                    </div>
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id ? "Downloading…" : "Download Report"}
                      </button>
                    ) : null}
                    {j.status === STATUS_SCHEDULED ? (
                      <button
                        type="button"
                        className="tool-btn-secondary"
                        onClick={() => handleCancel(j)}
                        disabled={cancellingJobId === j.job_id}
                      >
                        {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                      </button>
                    ) : null}
                    <JobInputActions
                      job={j}
                      onDownloadInput={handleDownloadInput}
                      downloadingInput={downloadingInputJobId === j.job_id}
                      onRestart={handleRestart}
                      restarting={restartingJobId === j.job_id}
                      minScheduledAt={minScheduledAt}
                    />
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default PriceUpdateTool;
